import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { RestApiResponse } from "@/types/common.api";
import { PaymentsPlan } from "@/types/users";

type InvoicePreviewParams = {
  subscriptionId: string;
  type: "base" | "addon";
  items: {
    id?: string;
    price: string;
    quantity: number;
  }[];
};

type InvoicePreviewResponse = {
  due_today: number;
  items?: {
    price: {
      recurring: {
        interval: "month" | "year";
      };
      lookup_key: string;
    };
    planName: string;
    quantity: number;
    unit_amount: number;
    description: string;
    amount: number;
    proration: boolean;
    period: {
      start: number;
      end: number;
    };
  }[];
};

type ChangeAddOnParams = {
  subscriptionId: string;
  items: {
    id?: string;
    price: string;
    quantity: number;
  }[];
};

type UpgradeSubscriptionParams = {
  subscriptionId: string;
  items: {
    price: string;
    quantity: number;
  }[];
  type?: "app" | "api";
};

export const paymentApi = createApi({
  reducerPath: "paymentApi",
  refetchOnFocus: false,
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/payment",
  }),
  tagTypes: ["Payment"],
  endpoints: (builder) => ({
    getPlans: builder.query<PaymentsPlan[], { product?: string }>({
      query: ({ product = "studio3.0" }) => ({
        url: `/plan?product=${product}`,
        method: "GET",
      }),
      transformResponse: async (res: RestApiResponse<any>) => {
        if (!res.success || !res.data) {
          return [];
        }
        return res.data;
      },
    }),
    getModelsByPlan: builder.query<RestApiResponse<any>, { product?: string }>({
      query: () => ({
        url: "/models",
        method: "GET",
      }),
      transformResponse: async (res: RestApiResponse<any>) => {
        if (!res.success || !res.data) {
          return [];
        }
        return res.data;
      },
    }),
    getInvoicePreview: builder.query<
      InvoicePreviewResponse,
      InvoicePreviewParams
    >({
      query: (params) => ({
        url: "/invoicePreview",
        method: "POST",
        body: params,
      }),
      transformResponse: (res: RestApiResponse<InvoicePreviewResponse>) => {
        if (!res.success || !res.data) {
          throw new Error("Failed to get invoice preview");
        }

        return res.data;
      },
      keepUnusedDataFor: 0,
    }),
    getBillingPortalUrl: builder.query<string, { successUrl: string }>({
      query: ({ successUrl }) => ({
        url: "/billingPortal",
        method: "POST",
        body: { successUrl },
      }),
      transformResponse: (res: RestApiResponse<string>) => {
        if (!res.success || !res.data) {
          throw new Error("Failed to get billing portal url");
        }

        return res.data;
      },
    }),
    changeAddOn: builder.mutation<unknown, ChangeAddOnParams>({
      query: (params) => ({
        url: "/addon",
        method: "POST",
        body: params,
      }),
      transformResponse: (res: RestApiResponse<unknown>) => {
        if (!res.success || !res.data) {
          throw new Error("Failed to change add on");
        }

        return res.data;
      },
    }),
    upgradeSubscription: builder.mutation<unknown, UpgradeSubscriptionParams>({
      query: (params) => ({
        url: "/subscriptions/custom/upgrade",
        method: "POST",
        body: params,
      }),
      transformResponse: (res: RestApiResponse<unknown>) => {
        if (!res.success || !res.data) {
          throw new Error("Failed to upgrade subscription");
        }

        return res.data;
      },
    }),
  }),
});
export const { useGetPlansQuery, useGetModelsByPlanQuery } = paymentApi;
